import {
  EditInvoiceFormValues,
  IInvoiceAddressDataSchema,
  IInvoiceContactUpdateSchema,
  IInvoiceRespSchema,
  InvoiceAddressDataSchema,
  InvoiceContactUpdateSchema,
  InvoiceStatusEnum,
  IOneTimeInvoicePatchSchema,
  IOneTimeInvoicePostDraftPatchSchema,
  NetTermsEnum,
  NetTermsEnumZ,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import { FormState } from 'react-hook-form';
import { CHANGE_CONTACT_STATUSES } from '../../constants/invoice';

export type EditInvoiceDirtyFields =
  FormState<EditInvoiceFormValues>['dirtyFields'];

/** The rich-text editors emit '' when cleared; the API expects null. */
const emptyToNull = (value?: string | null) => {
  const rawValue = value ?? null;
  return rawValue === '' ? null : rawValue;
};

/**
 * Fields a one-time invoice accepts in every status. Shared by the draft and
 * post-draft payloads so the two never drift.
 */
const buildEditableHeaderFields = (data: EditInvoiceFormValues) => ({
  purchaseOrderNumber: data.purchaseOrderNumber ?? null,
  vatNumber: data.vatNumber ?? null,
  registrationNumber: data.registrationNumber ?? null,
  memo: emptyToNull(data.invoiceMemo),
  invoiceTerms: emptyToNull(data.invoiceTerms),
  customFields: data.customFields ?? {},
});

/**
 * Payload for a one-time invoice that has left DRAFT. Only the fields the API
 * still accepts are sent - net terms, contacts, addresses and dates are
 * immutable once the invoice is finalized.
 */
export const buildOneTimeInvoicePostDraftPatchPayload = (
  data: EditInvoiceFormValues,
): IOneTimeInvoicePostDraftPatchSchema => buildEditableHeaderFields(data);

/**
 * Address payload for the dedicated address endpoint. When addresses are
 * sourced from the contacts the ids are dropped, since the backend resolves
 * them from the contact instead.
 */
export const buildInvoiceAddressPayload = (
  addressInformation: EditInvoiceFormValues['addressInformation'],
): IInvoiceAddressDataSchema => {
  const isContactAddressSource =
    addressInformation.addressSource ===
    QuoteSettingsDefaultAddressSourceEnum.CONTACT;
  return InvoiceAddressDataSchema.parse({
    addressSource: addressInformation.addressSource,
    billingAddressId: isContactAddressSource
      ? null
      : addressInformation.billingAddressId,
    shippingAddressId: isContactAddressSource
      ? null
      : addressInformation.shippingAddressId,
  });
};

/** Contact payload for the dedicated contact endpoint. */
export const buildInvoiceContactPayload = (
  data: EditInvoiceFormValues,
  addressPayload: IInvoiceAddressDataSchema,
): IInvoiceContactUpdateSchema =>
  InvoiceContactUpdateSchema.parse({
    contactId: data.billingContactId,
    shippingContactId: data.shippingContactId,
    addressInformation: addressPayload,
  });

/**
 * Contacts and addresses have their own endpoints, so they are only sent when
 * the user actually touched them.
 */
export const hasDirtyContactFields = (dirtyFields: EditInvoiceDirtyFields) =>
  !!dirtyFields.billingContactId || !!dirtyFields.shippingContactId;

export const hasDirtyAddressFields = (dirtyFields: EditInvoiceDirtyFields) =>
  !!dirtyFields.addressInformation?.addressSource ||
  !!dirtyFields.addressInformation?.billingAddressId ||
  !!dirtyFields.addressInformation?.shippingAddressId;

export const buildOneTimeInvoicePatchPayload = (
  invoice: IInvoiceRespSchema,
  data: EditInvoiceFormValues,
): IOneTimeInvoicePatchSchema => {
  const netTermsParsed = NetTermsEnumZ.safeParse(invoice.netTerms);
  const netTermsFromDaysParsed = NetTermsEnumZ.safeParse(
    `NET_${invoice.netTermsDays}`,
  );
  const netTerms = netTermsParsed.success
    ? netTermsParsed.data
    : netTermsFromDaysParsed.success
      ? netTermsFromDaysParsed.data
      : NetTermsEnum.NET_0;
  return {
    ...buildEditableHeaderFields(data),
    netTerms,
    billingContactId: data.billingContactId,
    shippingContactId: data.shippingContactId,
    locale: invoice.locale,
    invoicingDate: data.invoicingDate ?? invoice.invoicingDate,
    dueDate: data.dueDate ?? invoice.dueDate,
    addressInformation: {
      addressSource: data.addressInformation.addressSource,
      billingAddressId: data.addressInformation.billingAddressId ?? null,
      shippingAddressId: data.addressInformation.shippingAddressId ?? null,
    },
  };
};

type InvoiceEditability = {
  status: InvoiceStatusEnum;
  canUpdateBilling: boolean;
  creditAndRebill?: boolean;
};

/**
 * Header fields (PO/VAT/registration numbers, contacts, addresses, memo and
 * terms) are only mutable while the invoice is DRAFT or UNPAID. Past that,
 * invoice custom fields are the one thing still editable. Shared by the modal
 * and the section header so the edit affordance and the form agree on what is
 * locked.
 */
export const isInvoiceHeaderReadOnly = ({
  status,
  canUpdateBilling,
  creditAndRebill = false,
}: InvoiceEditability) =>
  !creditAndRebill &&
  (!canUpdateBilling || !CHANGE_CONTACT_STATUSES.has(status));

/**
 * Whether the edit modal would render anything the user can actually change.
 * A locked header plus no writable invoice custom fields leaves an empty body
 * and a Save that writes nothing, so the entry point is suppressed instead.
 *
 * `hasEditableCustomFields` must reflect WRITE permission, not merely READ -
 * read-only custom fields still render, but render disabled.
 */
export const hasEditableInvoiceContent = ({
  status,
  canUpdateBilling,
  creditAndRebill = false,
  hasEditableCustomFields,
}: InvoiceEditability & { hasEditableCustomFields: boolean }) => {
  if (!isInvoiceHeaderReadOnly({ status, canUpdateBilling, creditAndRebill })) {
    return true;
  }
  return canUpdateBilling && hasEditableCustomFields;
};
